/**
 * UsageByProjectChart Component
 *
 * Displays token usage breakdown by project as a horizontal bar chart.
 * Clicking a project drills down to show workflows within that project.
 *
 * Technical Design Reference: docs/technical/TD-AC-622-hierarchical-cost-breakdown.md (Section 4.4)
 * Related Tickets:
 * - AC-594 (Epic: Token Audit System)
 * - AC-622 (Epic: Hierarchical Cost Breakdown)
 * - AC-630 (Frontend: UsageByProjectChart component)
 * - AC-632 (Parent: UsageDashboard wiring)
 *
 * Parent Component: UsageDashboard
 * User Access Path: Settings > Usage Dashboard
 */

import React from 'react';
import Tooltip from '../common/Tooltip';
import type { TokenUsageByProject } from '../../types/tokenUsage';

/**
 * Props for the UsageByProjectChart component.
 */
export interface UsageByProjectChartProps {
  /** Project usage data */
  data: TokenUsageByProject[] | null;
  /** Loading state */
  loading?: boolean;
  /** Error message if fetch failed */
  error?: string | null;
  /** Callback when a project is clicked for drill-down */
  onProjectClick?: (project: TokenUsageByProject) => void;
  /** Optional CSS class name */
  className?: string;
}

/** Color palette for project bars */
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

/** Gray color for deleted projects */
const DELETED_COLOR = '#9CA3AF';

/**
 * Format a cost value as currency.
 * Uses adaptive precision: 4 decimal places for amounts under $1,
 * 2 decimal places for larger amounts.
 */
const formatCost = (value: number): string => {
  const decimalPlaces = value < 1 && value > 0 ? 4 : 2;
  return `$${value.toFixed(decimalPlaces)}`;
};

/**
 * Loading skeleton for the chart.
 */
const LoadingSkeleton: React.FC<{ className: string }> = ({ className }) => (
  <div
    data-testid="usage-by-project-loading"
    className={`bg-white dark:bg-cap-dark rounded-lg shadow p-6 ${className}`}
  >
    <div className="animate-pulse">
      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
      <div className="h-64 bg-gray-100 dark:bg-gray-700 rounded" />
    </div>
  </div>
);

/**
 * Error state component.
 */
const ErrorState: React.FC<{ error: string; className: string }> = ({ error, className }) => (
  <div
    data-testid="usage-by-project-error"
    className={`bg-white dark:bg-cap-dark rounded-lg shadow p-6 ${className}`}
  >
    <p className="text-red-500">Error: {error}</p>
  </div>
);

/**
 * Empty state when no data is available.
 */
const EmptyState: React.FC<{ className: string }> = ({ className }) => (
  <div
    data-testid="usage-by-project-empty"
    className={`bg-white dark:bg-cap-dark rounded-lg shadow p-6 ${className}`}
  >
    <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Usage by Project</h3>
    <p className="text-gray-500 dark:text-gray-400 text-center py-8">No project-attributed costs for this period</p>
  </div>
);

/**
 * UsageByProjectChart displays token usage breakdown by project as a horizontal bar chart.
 * Projects are sorted by total cost (highest first).
 * Clicking a project name triggers drill-down to show workflows within that project.
 *
 * @example
 * <UsageByProjectChart
 *   data={projectUsageData}
 *   loading={false}
 *   error={null}
 *   onProjectClick={(project) => handleProjectDrillDown(project)}
 *   className="mt-4"
 * />
 */
const UsageByProjectChart: React.FC<UsageByProjectChartProps> = ({
  data,
  loading = false,
  error = null,
  onProjectClick,
  className = '',
}) => {
  // Loading state
  if (loading) {
    return <LoadingSkeleton className={className} />;
  }

  // Error state
  if (error) {
    return <ErrorState error={error} className={className} />;
  }

  // Empty state
  if (!data || data.length === 0) {
    return <EmptyState className={className} />;
  }

  // Sort by cost descending (highest first)
  const sortedData = [...data].sort((a, b) => b.total_cost - a.total_cost);

  // Calculate max cost for scaling bars
  const maxCost = Math.max(...sortedData.map(p => p.total_cost));

  /**
   * Handle click on a project.
   * Does not allow drill-down on unattributed (null project_id).
   */
  const handleProjectClick = (project: TokenUsageByProject) => {
    // Don't allow drill-down on unattributed
    if (project.project_id === null) return;
    onProjectClick?.(project);
  };

  return (
    <div
      data-testid="usage-by-project-chart"
      className={`bg-white dark:bg-cap-dark rounded-lg shadow p-6 ${className}`}
    >
      <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Usage by Project</h3>

      {/* Bar Chart */}
      <div className="space-y-3">
        {sortedData.map((project, index) => {
          const barWidth = maxCost > 0 ? (project.total_cost / maxCost) * 100 : 0;
          const barColor = project.is_deleted ? DELETED_COLOR : COLORS[index % COLORS.length];
          const isClickable = onProjectClick && project.project_id !== null;

          return (
            <div key={project.project_id ?? 'unattributed'} className="flex items-center gap-4">
              {/* Project name - clickable for drill-down */}
              <Tooltip content={project.project_name}>
              <div
                className={`w-40 truncate text-sm ${
                  project.is_deleted ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'
                } ${isClickable ? 'cursor-pointer hover:text-primary-600' : ''}`}
                onClick={() => handleProjectClick(project)}
              >
                {project.project_name}
              </div>
              </Tooltip>

              {/* Bar */}
              <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.max(barWidth, 1)}%`,
                    backgroundColor: barColor,
                  }}
                />
              </div>

              {/* Cost label */}
              <div className="w-20 text-right text-sm font-medium text-gray-900 dark:text-warm-white">
                {formatCost(project.total_cost)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UsageByProjectChart;
